/**
 * @file DayDetailPage.js
 * @module packages/days/src/components/DayDetailPage
 * @description This module defines the DayDetailPage component, which is responsible for displaying
 * the detailed information for a single day within a trip. This includes the day's name (e.g., "Day 5"),
 * date, summary, and a notes editor.
 * 
 * This component is routed via `packages/main-app/src/App.js`.
 * Users typically navigate to this page by clicking on a day name in the `DaysList` component
 * (`packages/days/src/components/DaysList.jsx` which uses `DraggableDaysList.jsx`).
 * 
 * It utilizes:
 * - `useParams` from `react-router-dom` to get `tripId` and `dayId` from the URL.
 * - `useAuth` from `@trip-planner-monorepo/shared` for user authentication.
 * - `formatDate` from `@trip-planner-monorepo/shared` for date formatting.
 * - `Notes` component from `@trip-planner-monorepo/shared` for the rich text notes editor.
 * - `daysService` from `../services/daysService` to fetch all days in the trip (to find the specific day and its order).
 * - `updateDay` from `@trip-planner-monorepo/shared` (specifically the Firestore service function) to save notes for the day.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, useTrip, formatDate, Notes, updateDay, ContentBlockRenderer, RelatedDocumentsSection, EntityLinkerModal, LinkEntitiesButton, RelatedEntitiesSection, GenericConfirmModal, entityLinkingService, EntityDetailLayout, resolveDayItineraryContent } from '@trip-planner-monorepo/shared';
import { daysService } from '../services/daysService';
import { FaFile } from 'react-icons/fa';
import UploadModal from '@trip-planner-monorepo/documents/components/UploadModal';
import './DayDetailPage.css';

/**
 * @function DayDetailPage
 * @description A React functional component that renders the detail page for a specific day.
 * It fetches the day's details based on `tripId` and `dayId` from the URL parameters,
 * displays its information (day number, date, summary), and provides a notes editor.
 * 
 * @returns {JSX.Element} The rendered day detail page, or loading/error messages.
 */
const DayDetailPage = () => {
  const { tripId, dayId } = useParams();
  const { currentUser } = useAuth();
  const { effectiveUserId } = useTrip();
  const navigate = useNavigate();
  const [dayDetails, setDayDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [itineraryContent, setItineraryContent] = useState([]);

  // Unlink Modal State
  const [unlinkModalOpen, setUnlinkModalOpen] = useState(false);
  const [entityToUnlink, setEntityToUnlink] = useState(null);
  const [isUnlinking, setIsUnlinking] = useState(false);

  /**
   * @function fetchDayDetails
   * @description Fetches the details for the current day. It retrieves all days for the trip
   * to find the specific day by `dayId` and to determine its correct day number based on order.
   * Initializes the `notes` field for the day if it doesn't exist.
   * Wrapped in `useCallback` for performance optimization.
   */
  const fetchDayDetails = useCallback(async () => {
    if (!currentUser || !tripId || !dayId || !effectiveUserId) return;
    setLoading(true);
    setError(null); // Reset error before new fetch
    try {
      const allDays = await daysService.fetchDays(effectiveUserId, tripId);
      const currentDay = allDays.find(day => day.id === dayId);
      if (currentDay) {
        const dayIndex = allDays.sort((a, b) => a.order - b.order).findIndex(day => day.id === dayId);
        const { content } = await resolveDayItineraryContent(
          effectiveUserId,
          tripId,
          dayId,
          currentDay.content || []
        );
        setItineraryContent(content);
        setDayDetails({
          ...currentDay,
          dayNumber: dayIndex + 1,
          notes: currentDay.notes || '',
          lastEditedTime: currentDay.lastEditedTime // Ensure lastEditedTime is also passed
        });
      } else {
        setItineraryContent([]);
        setError('Day not found.');
      }
    } catch (err) {
      setError('Failed to load day details. Please try again.');
      console.error('Error fetching day details:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, tripId, dayId, effectiveUserId]);

  useEffect(() => {
    fetchDayDetails();
  }, [fetchDayDetails]);

  /**
   * @function handleSaveNotes
   * @description Saves the updated notes content for the current day.
   * It performs an optimistic update on the local state and then calls the `updateDay` service
   * to persist the changes to Firestore. If the save fails, it sets an error message and
   * re-fetches the day details to ensure data consistency.
   * @param {string} newNotesContent - The new HTML content of the notes.
   */
  const handleSaveNotes = async (newNotesContent) => {
    if (!currentUser || !tripId || !dayId || !dayDetails || !effectiveUserId) return;
    setError(null); // Reset error before new save
    try {
      const updatedTimestamp = new Date();
      setDayDetails(prevDetails => ({
        ...prevDetails,
        notes: newNotesContent,
        lastEditedTime: updatedTimestamp.toISOString() // Store as ISO string or consistently as Date object
      }));

      await updateDay(effectiveUserId, tripId, dayId, {
        notes: newNotesContent,
        lastEditedTime: updatedTimestamp.toISOString() // Persist timestamp
      });
    } catch (err) {
      setError('Failed to save notes. Please try again.');
      console.error('Error saving notes:', err);
      fetchDayDetails();
    }
  };

  /**
   * @function handleAccommodationClick
   * @description Handles navigation when user clicks on an accommodation link in day content.
   * @param {string} accommodationId - The ID of the accommodation to navigate to.
   */
  const handleAccommodationClick = (accommodationId) => {
    navigate(`/trips/${tripId}/accommodation/${accommodationId}`);
  };

  /**
   * @function handleTravelClick
   * @description Handles navigation when user clicks on a travel link in day content.
   * @param {string} travelId - The ID of the travel item to navigate to.
   */
  const handleTravelClick = (travelId) => {
    navigate(`/trips/${tripId}/travel/${travelId}`);
  };

  /**
   * @function handleActivityClick
   * @description Handles navigation when user clicks on an activity link in day content.
   * @param {string} activityId - The ID of the activity to navigate to.
   */
  const handleActivityClick = (activityId) => {
    navigate(`/trips/${tripId}/activities/${activityId}`);
  };

  /**
   * @function handlePlaceClick
   * @description Handles navigation when user clicks on a place link in day content.
   * @param {string} placeId - The ID of the place to navigate to.
   */
  const handlePlaceClick = (placeId) => {
    navigate(`/trips/${tripId}/places/${placeId}`);
  };

  /**
   * Handles opening the document upload modal
   */
  const handleDocumentUpload = () => {
    setShowUploadModal(true);
  };

  /**
   * Handles document upload completion
   */
  const handleDocumentProcessed = async (result) => {
    console.log('Document processed from day:', result);
    setShowUploadModal(false);
    // Refresh day data to show the newly uploaded document
    await fetchDayDetails();
  };

  /**
   * Handles entity link creation
   */
  const handleLinkCreated = async (links) => {
    console.log('Links created from day:', links);
    // Refresh day data to show the newly created links
    await fetchDayDetails();
  };

  /**
   * Handles unlink request - opens confirmation modal
   */
  const handleUnlinkRequest = (entityType, entityId, entityName) => {
    setEntityToUnlink({ entityType, entityId, entityName });
    setUnlinkModalOpen(true);
  };

  /**
   * Handles confirmed unlink - removes the link
   */
  const handleConfirmUnlink = async () => {
    if (!entityToUnlink || !currentUser || !tripId || !dayId || !effectiveUserId) return;

    setIsUnlinking(true);
    try {
      await entityLinkingService.removeLink(
        effectiveUserId,
        tripId,
        'day',
        dayId,
        entityToUnlink.entityType,
        entityToUnlink.entityId
      );

      // Refresh day data to reflect the removed link
      await fetchDayDetails();

      setUnlinkModalOpen(false);
      setEntityToUnlink(null);
    } catch (err) {
      console.error('Error unlinking entity:', err);
      setError('Failed to unlink entity. Please try again.');
    } finally {
      setIsUnlinking(false);
    }
  };

  // Prepare content for layout slots (only when data is available)

  // Primary Actions
  const primaryActions = dayDetails ? [
    <button
      key="upload"
      className="edit-button document-upload-button"
      onClick={handleDocumentUpload}
      title="Upload Document"
    >
      <FaFile /> Document Upload
    </button>
  ] : [];

  // Secondary Actions
  const secondaryActions = dayDetails ? [
    <LinkEntitiesButton
      key="link"
      className="edit-button"
      onClick={() => setShowLinkModal(true)}
    />
  ] : [];

  // Main Content Slot - Day Content blocks
  const mainContent = dayDetails ? (
    <div className="day-content-section">
      <h3>Day Content:</h3>
      <ContentBlockRenderer
        content={itineraryContent.filter(block => block.type !== 'document_reference')}
        onAccommodationClick={handleAccommodationClick}
        onTravelClick={handleTravelClick}
        onActivityClick={handleActivityClick}
        onPlaceClick={handlePlaceClick}
        onUnlinkAccommodation={(accommodationId, accommodationName) =>
          handleUnlinkRequest('accommodation', accommodationId, accommodationName)}
        tripId={tripId}
        className="day-content-renderer"
      />
    </div>
  ) : null;

  // Related Content Slot
  const relatedContent = dayDetails ? (
    <div className="entity-connections">
      <RelatedEntitiesSection
        contentBlocks={dayDetails.content}
        tripId={tripId}
        userId={effectiveUserId}
        hostEntityType="day"
        hostEntityId={dayId}
        excludeTypes={['document']}
        onUnlink={handleUnlinkRequest}
      />
      <RelatedDocumentsSection
        contentBlocks={dayDetails.content}
        tripId={tripId}
        userId={effectiveUserId}
        hostEntityType="day"
        hostEntityId={dayId}
        className="day-related-documents"
        onUnlink={handleUnlinkRequest}
      />
    </div>
  ) : null;

  // Notes Content Slot
  const notesContent = dayDetails ? (
    <Notes
      content={dayDetails.notes}
      onSave={handleSaveNotes}
      lastEditedTime={dayDetails.lastEditedTime ? new Date(dayDetails.lastEditedTime) : null}
      className="day-notes"
    />
  ) : null;

  const dayTitle = dayDetails ? `Day ${dayDetails.dayNumber}` : 'Day';
  const daySubtitle = dayDetails?.date ? formatDate(dayDetails.date) : 'Date not set';

  return (
    <>
      <EntityDetailLayout
        className="day-details"
        title={dayTitle}
        subtitle={daySubtitle}
        primaryActions={primaryActions}
        secondaryActions={secondaryActions}
        mainContent={mainContent}
        relatedContent={relatedContent}
        notesContent={notesContent}
        loading={loading}
        error={error}
      />

      {/* Document Upload Modal */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onProcessed={handleDocumentProcessed}
          currentEntity={{
            type: 'day',
            id: dayId,
            name: `Day ${dayDetails.dayNumber}: ${dayDetails.date ? formatDate(dayDetails.date) : 'Date not set'}`
          }}
        />
      )}

      {/* Entity Linker Modal */}
      {showLinkModal && dayDetails && (
        <EntityLinkerModal
          sourceEntity={{
            type: 'day',
            id: dayId,
            name: `Day ${dayDetails.dayNumber}: ${dayDetails.date ? formatDate(dayDetails.date) : 'Date not set'}`
          }}
          onLinkCreated={handleLinkCreated}
          onClose={() => setShowLinkModal(false)}
          allowMultiSelect={true}
        />
      )}

      {/* Unlink Confirmation Modal */}
      <GenericConfirmModal
        isOpen={unlinkModalOpen}
        onClose={() => setUnlinkModalOpen(false)}
        onConfirm={handleConfirmUnlink}
        title="Unlink Entity"
        message={
          entityToUnlink
            ? `Are you sure you want to unlink "${entityToUnlink.entityName}"? This will remove the link in both directions.`
            : 'Are you sure you want to unlink this entity?'
        }
        confirmText="Unlink"
        isDestructive={true}
        loading={isUnlinking}
      />
    </>
  );
};

export default DayDetailPage; 